/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { BEM, FONT_SIZE, FONT_SIZE_css_variable } from "@adapptio/lib-components";
import { overrideForwardRef } from "../OverridableComponent";
import { useDeepCompareMemo } from "use-deep-compare";

import "./Typography.scss";

export interface ITypographyProps {
	className?: string;
	children?: React.ReactNode;
	size?: FONT_SIZE | string;
	style?: React.CSSProperties;
	preferCssValue?: boolean;
	noWrap?: boolean;
	truncate?: boolean;
	italic?: boolean;
}

export const Typography = overrideForwardRef<ITypographyProps, "span">(
	(
		{ children, className, Component, size, truncate, italic, noWrap, preferCssValue, style, ...props },
		ref
	) => {
		const enumSize = Object.values(FONT_SIZE).some((enumFontSize) => enumFontSize === size);
		const baseClass = BEM("edt-typography", null, {
			size: enumSize ? size.replace("-", "") : size,
			nowrap: noWrap,
			truncate,
			italic
		});

		const componentStyle = useDeepCompareMemo<React.CSSProperties>(() => {
			if (enumSize) {
				if (!preferCssValue) {
					return style;
				}

				const fontSizeVariable = `--font-size-${size}` as FONT_SIZE_css_variable;
				const fontSize = getComputedStyle(document?.documentElement).getPropertyValue(
					fontSizeVariable
				);

				if (!fontSize) {
					return style;
				}

				return {
					...style,
					fontSize
				};
			}

			return {
				...style,
				fontSize: size
			};
		}, [ enumSize, style, size, preferCssValue ]);

		if (className) {
			baseClass.add(className);
		}

		return (
			<Component {...props} className={baseClass.toClassName()} style={componentStyle} ref={ref}>
				{children}
			</Component>
		);
	}
);

Typography.defaultProps = {
	Component: "span"
};
